import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bot, Loader2, LogIn, Plus, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ChatHeader from '../components/Chat/ChatHeader';
import MessageInput from '../components/Chat/MessageInput';
import MessageList from '../components/Chat/MessageList';
import Header from '../components/Layout/Header';
import Sidebar from '../components/Layout/Sidebar';
import type { Conversation, Message } from '../types';
import { useNotificationSound } from '../hooks/useNotificationSound';
import { allyWebSocket } from '../services/websocket';
import { primeNotifications } from '../services/notifications';
import { useAuthStore } from '../stores/authStore';
import { useChatStore } from '../stores/chatStore';
import { cn, ui } from '../utils';
import { buildAsyncJobSummariesByConversation, getPendingAsyncJobPlaceholderMessages } from '../utils/asyncJobs';

const GroupChatModal = React.lazy(() => import('../components/Layout/GroupChatModal').then((m) => ({ default: m.GroupChatModal })));
const InfoPanel = React.lazy(() => import('../components/Layout/InfoPanel').then((m) => ({ default: m.InfoPanel })));
const OnboardingModal = React.lazy(() => import('../components/Onboarding/OnboardingModal').then((m) => ({ default: m.OnboardingModal })));

const FOREGROUND_REFRESH_DEBOUNCE_MS = 750;
const FOREGROUND_REFRESH_COOLDOWN_MS = 5_000;

function preservePendingAsyncJobState(state: {
  conversations: Conversation[];
  messages: Record<string, Message[]>;
  activeConversationId: string | null;
}) {
  const preservedMessages = Object.entries(state.messages).reduce<Record<string, Message[]>>((next, [conversationId, messages]) => {
    const placeholders = getPendingAsyncJobPlaceholderMessages(messages);
    if (placeholders.length > 0) {
      next[conversationId] = placeholders;
    }
    return next;
  }, {});
  const preservedConversationIds = new Set(Object.keys(preservedMessages));

  return {
    messages: preservedMessages,
    conversations: state.conversations.filter((conversation) => preservedConversationIds.has(conversation.id)),
    activeConversationId: state.activeConversationId && preservedConversationIds.has(state.activeConversationId)
      ? state.activeConversationId
      : null,
    asyncJobSummaries: buildAsyncJobSummariesByConversation(preservedMessages),
  };
}

function getAuthIdentityKey(options: {
  userId?: string | null;
  userEmail?: string | null;
  impersonatedUserId?: string | null;
  impersonatedUserEmail?: string | null;
  viewAsUserId?: string | null;
  viewAsUserEmail?: string | null;
}) {
  return [
    options.viewAsUserId || '',
    options.viewAsUserEmail || '',
    options.impersonatedUserId || '',
    options.impersonatedUserEmail || '',
    options.userId || '',
    options.userEmail || '',
  ].join('::');
}

export function ChatPage() {
  const activeConversationId = useChatStore((state) => state.activeConversationId);
  const conversations = useChatStore((state) => state.conversations);
  const messages = useChatStore((state) => state.messages);
  const agents = useChatStore((state) => state.agents);
  const error = useChatStore((state) => state.error);
  const searchResults = useChatStore((state) => state.searchResults);
  const clearError = useChatStore((state) => state.clearError);
  const loadConversations = useChatStore((state) => state.loadConversations);
  const loadAgents = useChatStore((state) => state.loadAgents);
  const fetchAgentStatuses = useChatStore((state) => state.fetchAgentStatuses);
  const sendSuggestion = useChatStore((state) => state.sendSuggestion);
  const loadTags = useChatStore((state) => state.loadTags);
  const initializeOfflineQueue = useChatStore((state) => state.initializeOfflineQueue);
  const setOfflineStatus = useChatStore((state) => state.setOfflineStatus);
  const flushOfflineQueue = useChatStore((state) => state.flushOfflineQueue);
  const isOffline = useChatStore((state) => state.isOffline);
  const isFlushingOfflineQueue = useChatStore((state) => state.isFlushingOfflineQueue);
  const setActiveConversation = useChatStore((state) => state.setActiveConversation);
  const createConversation = useChatStore((state) => state.createConversation);
  const createGroupConversation = useChatStore((state) => state.createGroupConversation);
  const renameConversation = useChatStore((state) => state.renameConversation);
  const deleteConversation = useChatStore((state) => state.deleteConversation);
  const ensureDefaultConversation = useChatStore((state) => state.ensureDefaultConversation);
  const resumePendingJobs = useChatStore((state) => state.resumePendingJobs);

  const user = useAuthStore((state) => state.user);
  const impersonatedUser = useAuthStore((state) => state.impersonatedUser);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const isAuthLoading = useAuthStore((state) => state.isLoading);
  const connectionState = useAuthStore((state) => state.connectionState);
  const { login, error: authError } = useAuth();

  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isInfoPanelOpen, setIsInfoPanelOpen] = useState(false);
  const [isGroupChatModalOpen, setIsGroupChatModalOpen] = useState(false);
  const [isViewReset, setIsViewReset] = useState(false);
  const [highlightedMessageId, setHighlightedMessageId] = useState<string | null>(null);
  const [isOnboardingOpen, setIsOnboardingOpen] = useState(false);
  const foregroundRefreshTimeoutRef = useRef<number | null>(null);
  const lastForegroundRefreshAtRef = useRef(0);
  const previousAuthIdentityKeyRef = useRef<string | null>(null);
  const viewAsUser = useAuthStore((state) => state.viewAsUser);
  const authIdentityKey = useMemo(() => getAuthIdentityKey({
    userId: user?.id,
    userEmail: user?.email,
    impersonatedUserId: impersonatedUser?.id,
    impersonatedUserEmail: impersonatedUser?.email,
    viewAsUserId: viewAsUser?.id,
    viewAsUserEmail: viewAsUser?.email,
  }), [impersonatedUser?.email, impersonatedUser?.id, user?.email, user?.id, viewAsUser?.email, viewAsUser?.id]);

  useNotificationSound(messages);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    if (connectionState === 'disconnected') {
      void allyWebSocket.connect('main').catch(console.error);
    }
  }, [connectionState, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) {
      previousAuthIdentityKeyRef.current = null;
      return;
    }

    const canPreservePendingJobs = previousAuthIdentityKeyRef.current === null || previousAuthIdentityKeyRef.current === authIdentityKey;
    const preservedAsyncJobState = canPreservePendingJobs
      ? preservePendingAsyncJobState(useChatStore.getState())
      : {
          conversations: [],
          messages: {},
          activeConversationId: null,
          asyncJobSummaries: {},
        };

    useChatStore.setState({
      conversations: preservedAsyncJobState.conversations,
      messages: preservedAsyncJobState.messages,
      asyncJobSummaries: preservedAsyncJobState.asyncJobSummaries,
      streamingTokens: {},
      streamingMessageIds: {},
      typing: {},
      activeConversationId: preservedAsyncJobState.activeConversationId,
      agentStatuses: {},
      tags: [],
      activeTagFilterIds: [],
      searchResults: [],
      searchTotal: 0,
      searchHasMore: false,
      searchError: null,
      error: null,
      composerDrafts: {},
      suggestionDispatchVersion: {},
    });
    previousAuthIdentityKeyRef.current = authIdentityKey;
    setIsViewReset(false);
    setHighlightedMessageId(null);

    resumePendingJobs();
    initializeOfflineQueue();
    void loadConversations();
    void loadAgents();
    void loadTags();
    void primeNotifications();
  }, [authIdentityKey, initializeOfflineQueue, isAuthenticated, loadAgents, loadConversations, loadTags, resumePendingJobs]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    void fetchAgentStatuses();
    const intervalId = window.setInterval(() => {
      void fetchAgentStatuses();
    }, 180_000);

    return () => window.clearInterval(intervalId);
  }, [fetchAgentStatuses, isAuthenticated]);

  const scheduleForegroundRefresh = useCallback(() => {
    if (!isAuthenticated || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    if (document.visibilityState === 'hidden') {
      return;
    }

    if (foregroundRefreshTimeoutRef.current) {
      window.clearTimeout(foregroundRefreshTimeoutRef.current);
    }

    foregroundRefreshTimeoutRef.current = window.setTimeout(() => {
      const now = Date.now();
      if (now - lastForegroundRefreshAtRef.current < FOREGROUND_REFRESH_COOLDOWN_MS) {
        return;
      }

      lastForegroundRefreshAtRef.current = now;
      void Promise.allSettled([loadConversations(), fetchAgentStatuses()]).finally(() => {
        resumePendingJobs();
      });
    }, FOREGROUND_REFRESH_DEBOUNCE_MS);
  }, [fetchAgentStatuses, isAuthenticated, loadConversations, resumePendingJobs]);

  useEffect(() => {
    if (!isAuthenticated || typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    const handleForegroundRefresh = () => {
      scheduleForegroundRefresh();
    };

    window.addEventListener('focus', handleForegroundRefresh);
    window.addEventListener('pageshow', handleForegroundRefresh);
    document.addEventListener('visibilitychange', handleForegroundRefresh);

    return () => {
      window.removeEventListener('focus', handleForegroundRefresh);
      window.removeEventListener('pageshow', handleForegroundRefresh);
      document.removeEventListener('visibilitychange', handleForegroundRefresh);
      if (foregroundRefreshTimeoutRef.current) {
        window.clearTimeout(foregroundRefreshTimeoutRef.current);
      }
    };
  }, [isAuthenticated, scheduleForegroundRefresh]);

  useEffect(() => {
    if (!isAuthenticated) {
      setIsOnboardingOpen(false);
      return;
    }

    try {
      setIsOnboardingOpen(localStorage.getItem('ally_onboarding_complete') !== 'true');
    } catch {
      setIsOnboardingOpen(false);
    }
  }, [isAuthenticated, impersonatedUser?.email, impersonatedUser?.id, user?.email, user?.id]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const handleOffline = () => setOfflineStatus(true);
    const handleOnline = () => {
      setOfflineStatus(false);
      void flushOfflineQueue().finally(() => {
        resumePendingJobs();
      });
    };

    setOfflineStatus(typeof navigator !== 'undefined' ? navigator.onLine === false : false);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, [flushOfflineQueue, isAuthenticated, resumePendingJobs, setOfflineStatus]);

  const sortedConversations = useMemo(
    () => [...conversations].sort((left, right) => new Date(right.lastActivity).getTime() - new Date(left.lastActivity).getTime()),
    [conversations],
  );
  const mobileSidebarOffsetClass = impersonatedUser ? 'top-[84px]' : 'top-[60px]';
  const mobileSidebarHeightClass = impersonatedUser ? 'h-[calc(100%-84px)]' : 'h-[calc(100%-60px)]';

  const activeConversation = useMemo(
    () => sortedConversations.find((conversation) => conversation.id === activeConversationId) ?? null,
    [activeConversationId, sortedConversations],
  );

  const getMostRecentConversationForAgent = useCallback((agentId: string) => {
    const existingConversations = conversations
      .filter((conversation) => conversation.agentId === agentId && !conversation.isGroup)
      .sort((left, right) => new Date(right.lastActivity).getTime() - new Date(left.lastActivity).getTime());

    return existingConversations[0] ?? null;
  }, [conversations]);

  const activeAgent = useMemo(() => {
    if (!activeConversation || activeConversation.isGroup) {
      return null;
    }

    return agents.find((agent) => agent.id === activeConversation.agentId) ?? null;
  }, [activeConversation, agents]);

  const onboardingAgents = useMemo(
    () => agents.filter((agent) => !agent.owner || agent.type !== 'user').slice(0, 4),
    [agents],
  );

  useEffect(() => {
    if (!isAuthenticated || isViewReset) {
      return;
    }

    if (!activeConversationId && sortedConversations.length > 0) {
      setActiveConversation(sortedConversations[0].id);
    }
  }, [activeConversationId, isAuthenticated, isViewReset, setActiveConversation, sortedConversations]);

  useEffect(() => {
    if (!isAuthenticated || isViewReset) {
      return;
    }

    if (!activeConversationId && agents[0]?.id) {
      ensureDefaultConversation(agents[0].id);
    }
  }, [activeConversationId, agents, ensureDefaultConversation, isAuthenticated, isViewReset]);

  const openConversation = (conversationId: string, messageId?: string) => {
    const existingConversation = useChatStore.getState().conversations.find((conversation) => conversation.id === conversationId);
    if (!existingConversation) {
      const searchResult = useChatStore.getState().searchResults.find((result) => result.conversationId === conversationId);
      if (searchResult) {
        const fallbackConversation: Conversation = {
          id: searchResult.conversationId,
          title: searchResult.conversationTitle || 'Untitled conversation',
          agentId: searchResult.agentId ?? undefined,
          agentName: searchResult.agentName ?? undefined,
          lastActivity: searchResult.conversationUpdatedAt || searchResult.messageCreatedAt,
          unreadCount: 0,
          archivedAt: searchResult.conversationArchivedAt ?? null,
          tags: searchResult.tags ?? [],
          isPinned: false,
          pinOrder: null,
        };

        useChatStore.setState((state) => ({
          conversations: [fallbackConversation, ...state.conversations],
        }));
      }
    }

    setIsViewReset(false);
    setHighlightedMessageId(messageId ?? null);
    setActiveConversation(conversationId);
    setIsSidebarOpen(false);
  };

  const handleAgentSelect = async (agentId: string) => {
    const latestConversation = getMostRecentConversationForAgent(agentId);

    if (latestConversation) {
      openConversation(latestConversation.id);
      return;
    }

    const conversationId = await createConversation(agentId, agents.find((agent) => agent.id === agentId)?.name);
    openConversation(conversationId);
  };

  const handleNewConversation = async (agentId: string) => {
    const conversationId = await createConversation(agentId, agents.find((agent) => agent.id === agentId)?.name);
    openConversation(conversationId);
  };

  const handleRoutingSuggestion = async ({ agentId, agentName, userQuestion }: { agentId: string; agentName?: string; userQuestion: string }) => {
    const conversationId = await createConversation(agentId, agentName || agents.find((agent) => agent.id === agentId)?.name || agentId);
    openConversation(conversationId);
    sendSuggestion(userQuestion, conversationId);
  };

  const handleNewSession = async () => {
    if (!activeAgent) return;
    await handleNewConversation(activeAgent.id);
  };

  const handleResetView = async () => {
    setIsViewReset(true);
    setHighlightedMessageId(null);
    setActiveConversation(null);
    setIsInfoPanelOpen(false);
  };

  const handleCreateGroupChat = async (name: string, agentIds: string[]) => {
    const conversationId = await createGroupConversation(name, agentIds);
    openConversation(conversationId);
    setIsSidebarOpen(false);
  };

  const handleOnboardingComplete = async (suggestionText?: string) => {
    try {
      localStorage.setItem('ally_onboarding_complete', 'true');
    } catch {
      // ignore storage failures
    }

    setIsOnboardingOpen(false);

    const prompt = String(suggestionText || '').trim();
    if (!prompt) {
      return;
    }

    let conversationId = activeConversationId;
    if (!conversationId) {
      const fallbackAgentId = onboardingAgents[0]?.id || agents[0]?.id;
      if (!fallbackAgentId) {
        return;
      }
      conversationId = await createConversation(fallbackAgentId, agents.find((agent) => agent.id === fallbackAgentId)?.name);
      openConversation(conversationId);
    }

    sendSuggestion(prompt, conversationId);
  };

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey) || event.key.toLowerCase() !== 'n') {
        return;
      }

      event.preventDefault();
      if (activeAgent?.id) {
        void handleNewConversation(activeAgent.id);
        return;
      }

      if (agents[0]?.id) {
        void handleNewConversation(agents[0].id);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeAgent?.id, agents, isAuthenticated]);

  if (isAuthLoading) {
    return (
      <div className="flex h-full items-center justify-center bg-background px-6 text-foreground">
        <div className="w-full max-w-md rounded-[2rem] border border-border bg-card px-8 py-10 text-center shadow-2xl shadow-black/10">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-600/15 text-blue-300">
            <Loader2 className="h-7 w-7 animate-spin" />
          </div>
          <h1 className="text-2xl font-semibold text-foreground-bright">Checking your Microsoft session</h1>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            Hang tight while Ally restores your sign-in and gets your workspace ready.
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !user) {
    return (
      <div className="flex h-full items-center justify-center bg-background px-6 text-foreground">
        <div className="w-full max-w-lg rounded-[2rem] border border-border bg-card px-8 py-10 text-center shadow-2xl shadow-black/10">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-300">
            <ShieldCheck className="h-8 w-8" />
          </div>
          <h1 className="text-3xl font-semibold text-foreground-bright">Sign in to AFN Ally</h1>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            Use your Microsoft work account to unlock chat, agents, and synced sessions.
          </p>
          {authError ? (
            <div className="mt-5 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
              {authError}
            </div>
          ) : null}
          <button
            type="button"
            onClick={() => void login()}
            className={cn(ui.button.base, ui.button.primary, 'mt-6 rounded-xl px-5 py-3')}
          >
            <LogIn className="h-4 w-4" />
            Sign in with Microsoft
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', overflow: 'hidden' }} className="flex-col bg-background text-foreground">
      <a href="#chat-main-content" className="skip-link">Skip to conversation</a>
      <Header
        onMenuClick={() => setIsSidebarOpen((open) => !open)}
        isSidebarOpen={isSidebarOpen}
        agents={agents}
        conversations={sortedConversations}
        messagesByConversation={messages}
        onAgentSelect={(agentId) => void handleAgentSelect(agentId)}
        onConversationSelect={(conversationId, messageId) => {
          openConversation(conversationId, messageId);
        }}
      />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <Sidebar
          agents={agents}
          conversations={sortedConversations}
          currentConversationId={activeConversationId}
          onAgentSelect={(agentId) => void handleAgentSelect(agentId)}
          onNewConversation={(agentId) => void handleNewConversation(agentId)}
          onConversationSelect={(conversationId) => {
            openConversation(conversationId);
          }}
          onNewGroupChat={() => setIsGroupChatModalOpen(true)}
          isOpen={isSidebarOpen}
          mobileOffsetClassName={mobileSidebarOffsetClass}
          mobileHeightClassName={mobileSidebarHeightClass}
        />

        <main id="chat-main-content" className="flex min-h-0 min-w-0 flex-1 flex-col bg-background" tabIndex={-1}>
          {activeConversation ? (
            <>
              <ChatHeader
                agent={activeAgent}
                conversation={activeConversation}
                messages={messages[activeConversation.id] || []}
                onToggleInfoPanel={() => setIsInfoPanelOpen((open) => !open)}
                onNewSession={() => void handleNewSession()}
                onRenameConversation={(title) => renameConversation(activeConversation.id, title)}
              />

              {isOffline ? (
                <div className="shrink-0 border-b border-amber-500/20 bg-amber-500/10 px-6 py-3">
                  <div className="flex items-center justify-between gap-4 text-sm text-amber-100">
                    <div>You're offline, messages will be sent when you reconnect.</div>
                    {isFlushingOfflineQueue ? <div className="text-xs uppercase tracking-wide text-amber-200/80">Reconnecting…</div> : null}
                  </div>
                </div>
              ) : null}

              {error ? (
                <div className="shrink-0 border-b border-red-500/20 bg-red-500/10 px-6 py-3">
                  <div className="flex items-center justify-between gap-4">
                    <div className="text-sm text-red-200">{error}</div>
                    <button type="button" onClick={clearError} className="rounded-lg p-1 text-red-300 transition-colors duration-150 hover:bg-red-500/10 hover:text-white" aria-label="Dismiss error banner">
                      ×
                    </button>
                  </div>
                </div>
              ) : null}

              <div className="min-h-0 flex-1 overflow-hidden">
                <MessageList
                  conversationId={activeConversation.id}
                  highlightedMessageId={highlightedMessageId}
                  className="h-full min-h-0"
                  onSuggestionSelect={(text) => sendSuggestion(text, activeConversation.id)}
                  onRoutingSuggestionSelect={(suggestion) => void handleRoutingSuggestion(suggestion)}
                />
              </div>
              <MessageInput
                conversationId={activeConversation.id}
                placeholder={activeConversation.isGroup ? 'Mention agents with @name to route your message...' : `Message ${activeAgent?.name || 'Polaris Orchestrator'}...`}
              />
            </>
          ) : (
            <div className="flex flex-1 items-center justify-center px-6">
              <div className="max-w-lg rounded-[2rem] border border-border bg-card px-10 py-12 text-center shadow-2xl shadow-black/10">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-600/15 text-blue-300">
                  <Plus size={30} />
                </div>
                <h2 className="text-2xl font-semibold text-foreground-bright">{isViewReset ? 'View reset' : 'Open a conversation'}</h2>
                <p className="mt-3 text-sm leading-6 text-muted-foreground">
                  {isViewReset
                    ? 'The chat screen is clear. Pick a saved conversation or start a fresh chat. Your history is still preserved.'
                    : 'Choose an agent from the sidebar to jump back into your latest thread, or start a fresh chat.'}
                </p>
                <button
                  type="button"
                  onClick={() => agents[0] && void handleAgentSelect(agents[0].id)}
                  className={cn(ui.button.base, ui.button.primary, 'mt-6 rounded-xl px-5 py-3')}
                >
                  <Bot size={18} />
                  Message {agents[0]?.name || 'Polaris Orchestrator'}
                </button>
              </div>
            </div>
          )}
        </main>

        {isInfoPanelOpen ? (
          <React.Suspense fallback={<div>Loading...</div>}>
            <InfoPanel
              agent={activeAgent}
              conversation={activeConversation}
              conversations={sortedConversations}
              isOpen={isInfoPanelOpen}
              onClose={() => setIsInfoPanelOpen(false)}
              onSelectConversation={(conversationId) => {
                openConversation(conversationId);
              }}
              onNewChat={() => void handleNewSession()}
              onDeleteConversation={(conversationId) => void deleteConversation(conversationId)}
              onResetView={() => handleResetView()}
            />
          </React.Suspense>
        ) : null}
      </div>

      {isGroupChatModalOpen ? (
        <React.Suspense fallback={<div>Loading...</div>}>
          <GroupChatModal
            isOpen={isGroupChatModalOpen}
            agents={agents}
            onClose={() => setIsGroupChatModalOpen(false)}
            onCreate={handleCreateGroupChat}
          />
        </React.Suspense>
      ) : null}

      {isOnboardingOpen ? (
        <React.Suspense fallback={<div>Loading...</div>}>
          <OnboardingModal
            isOpen={isOnboardingOpen}
            agents={onboardingAgents.length > 0 ? onboardingAgents : agents.slice(0, 4)}
            onSkip={() => void handleOnboardingComplete()}
            onComplete={(suggestionText) => void handleOnboardingComplete(suggestionText)}
          />
        </React.Suspense>
      ) : null}

      {isSidebarOpen ? (
        <button
          type="button"
          className={`fixed inset-0 z-30 bg-black/40 backdrop-blur-[1px] transition-opacity duration-150 lg:hidden ${mobileSidebarOffsetClass}`}
          aria-label="Close sidebar"
          onClick={() => setIsSidebarOpen(false)}
        />
      ) : null}
    </div>
  );
}

export default ChatPage;
